import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Tabs, Descriptions, Tag, Table, Modal, Typography, Space, Popconfirm, InputNumber } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, EditOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { apiTokensAPI } from '../services/api';

const { TabPane } = Tabs;

const Profile = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('view');
  const { user, updateProfile, changePassword } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [apiTokenForm] = Form.useForm();

  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [createdToken, setCreatedToken] = useState(null);
  const [createdTokenModalOpen, setCreatedTokenModalOpen] = useState(false);

  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'red',
      'admin': 'orange',
      'client': 'blue',
      'viewer': 'green',
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'client': 'Client',
      'viewer': 'Viewer',
    };
    return labels[role] || role;
  };

  const onProfileUpdate = async (values) => {
    setLoading(true);
    try {
      const result = await updateProfile(values);
      if (result.success) {
        message.success('Profile updated successfully!');
        setActiveTab('view');
      } else {
        message.error(result.error);
      }
    } catch (error) {
      message.error('Profile update failed');
    } finally {
      setLoading(false);
    }
  };

  const onPasswordChange = async (values) => {
    setLoading(true);
    try {
      const result = await changePassword(values.currentPassword, values.newPassword);
      if (result.success) {
        message.success('Password changed successfully!');
        passwordForm.resetFields();
        setActiveTab('view');
      } else {
        message.error(result.error);
      }
    } catch (error) {
      message.error('Password change failed');
    } finally {
      setLoading(false);
    }
  };

  // Initialize form values when switching to edit tab
  const handleTabChange = (key) => {
    if (key === 'edit') {
      profileForm.setFieldsValue({
        username: user.username,
        email: user.email,
      });
    }
    if (key === 'tokens') {
      loadTokens();
    }
    setActiveTab(key);
  };

  const loadTokens = async () => {
    setTokensLoading(true);
    try {
      const res = await apiTokensAPI.list();
      setTokens(res.data?.tokens || []);
    } catch (error) {
      message.error('Failed to load API tokens');
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tokens') {
      loadTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreateApiToken = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        expires_in_days: values.expires_in_days || undefined,
      };
      const res = await apiTokensAPI.create(payload);
      const token = res.data?.token;
      if (token) {
        setCreatedToken(token);
        setCreatedTokenModalOpen(true);
        apiTokenForm.resetFields();
        await loadTokens();
      } else {
        message.error('Token created, but was not returned');
      }
    } catch (error) {
      message.error(error?.response?.data?.error || 'Failed to create API token');
    } finally {
      setLoading(false);
    }
  };

  const onRevokeApiToken = async (tokenId) => {
    setTokensLoading(true);
    try {
      await apiTokensAPI.revoke(tokenId);
      message.success('Token revoked');
      await loadTokens();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Failed to revoke token');
    } finally {
      setTokensLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <Card title="User Profile">
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="View Profile" key="view">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Username">
                <UserOutlined style={{ marginRight: 8 }} />
                {user.username}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <MailOutlined style={{ marginRight: 8 }} />
                {user.email}
              </Descriptions.Item>
              <Descriptions.Item label="Role">
                <Tag color={getRoleColor(user.role)}>
                  {getRoleLabel(user.role)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(user.created_at).toLocaleDateString()}
              </Descriptions.Item>
              {user.last_login && (
                <Descriptions.Item label="Last Login">
                  {new Date(user.last_login).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>
            
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setActiveTab('edit')}
                style={{ marginRight: 16 }}
              >
                Edit Profile
              </Button>
              <Button
                icon={<LockOutlined />}
                onClick={() => setActiveTab('password')}
              >
                Change Password
              </Button>
            </div>
          </TabPane>

          <TabPane tab="Edit Profile" key="edit">
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={onProfileUpdate}
              initialValues={{
                username: user.username,
                email: user.email,
              }}
            >
              <Form.Item
                name="username"
                label="Username"
                rules={[
                  {
                    required: true,
                    message: 'Please input your username!',
                  },
                  {
                    min: 3,
                    message: 'Username must be at least 3 characters!',
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Username"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    required: true,
                    message: 'Please input your email!',
                  },
                  {
                    type: 'email',
                    message: 'Please enter a valid email!',
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  style={{ marginRight: 16 }}
                >
                  Update Profile
                </Button>
                <Button
                  size="large"
                  onClick={() => setActiveTab('view')}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Change Password" key="password">
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={onPasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[
                  {
                    required: true,
                    message: 'Please input your current password!',
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Current Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  {
                    required: true,
                    message: 'Please input your new password!',
                  },
                  {
                    min: 6,
                    message: 'Password must be at least 6 characters!',
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="New Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your new password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm New Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  style={{ marginRight: 16 }}
                >
                  Change Password
                </Button>
                <Button
                  size="large"
                  onClick={() => setActiveTab('view')}
                >
                  Cancel
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="API Tokens" key="tokens">
            <div style={{ marginBottom: 16 }}>
              <Form
                form={apiTokenForm}
                layout="vertical"
                onFinish={onCreateApiToken}
              >
                <Form.Item
                  name="name"
                  label="Token Name"
                  rules={[{ required: true, message: 'Please enter a token name' }]}
                >
                  <Input placeholder="e.g. CI token, Postman token" size="large" />
                </Form.Item>

                <Form.Item
                  name="expires_in_days"
                  label="Expires In (days)"
                  tooltip="Optional. Leave blank for no expiry."
                >
                  <InputNumber min={1} style={{ width: '100%' }} placeholder="Optional" size="large" />
                </Form.Item>

                <Button type="primary" htmlType="submit" loading={loading}>
                  Generate Token
                </Button>
              </Form>
            </div>

            <Table
              size="small"
              rowKey="id"
              loading={tokensLoading}
              dataSource={tokens}
              pagination={false}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                {
                  title: 'Created',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (v) => (v ? new Date(v).toLocaleString() : '-'),
                },
                {
                  title: 'Last Used',
                  dataIndex: 'last_used_at',
                  key: 'last_used_at',
                  render: (v) => (v ? new Date(v).toLocaleString() : '-'),
                },
                {
                  title: 'Expires',
                  dataIndex: 'expires_at',
                  key: 'expires_at',
                  render: (v) => (v ? new Date(v).toLocaleString() : 'Never'),
                },
                {
                  title: 'Status',
                  dataIndex: 'is_revoked',
                  key: 'is_revoked',
                  render: (revoked) => (revoked ? <Tag color="red">Revoked</Tag> : <Tag color="green">Active</Tag>),
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Space>
                      <Popconfirm
                        title="Revoke this token?"
                        description="This cannot be undone."
                        okText="Revoke"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onRevokeApiToken(record.id)}
                        disabled={record.is_revoked}
                      >
                        <Button danger disabled={record.is_revoked}>
                          Revoke
                        </Button>
                      </Popconfirm>
                    </Space>
                  ),
                },
              ]}
            />

            <Modal
              title="New API Token"
              open={createdTokenModalOpen}
              onOk={() => {
                setCreatedTokenModalOpen(false);
                setCreatedToken(null);
              }}
              onCancel={() => {
                setCreatedTokenModalOpen(false);
                setCreatedToken(null);
              }}
              okText="Done"
            >
              <Typography.Paragraph>
                Copy this token now — you won’t be able to see it again.
              </Typography.Paragraph>
              <Typography.Paragraph>
                <Typography.Text copyable>{createdToken || ''}</Typography.Text>
              </Typography.Paragraph>
              <Typography.Paragraph>
                Use it as <Typography.Text code>Authorization: Bearer &lt;token&gt;</Typography.Text>
              </Typography.Paragraph>
            </Modal>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Profile;
